import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrganizations } from '../store/slices/organizationsSlice';
import { authAPI } from '../services/api';
import Modal from '../components/common/Modal';
import { Loading, EmptyState, ConfirmDialog } from '../components/common';
import { USER_ROLES } from '../utils/constants';
import toast from 'react-hot-toast';
import { UsersIcon, PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const Users = () => {
  const dispatch = useDispatch();
  const { organizations } = useSelector((state) => state.organizations);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  
  const [formData, setFormData] = useState({
    username: '', password: '', password_confirm: '', first_name: '', last_name: '', email: '', phone: '',
    role: 'otm_auditor', organization: '', position: '', is_active: true
  });

  useEffect(() => {
    dispatch(fetchOrganizations());
    fetchUsers();
  }, [dispatch]);

  const fetchUsers = async () => {
    try {
      const response = await authAPI.getUsers();
      setUsers(response.data.results || response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password && formData.password !== formData.password_confirm) {
      toast.error('Parollar mos kelmaydi');
      return;
    }
    try {
      if (selectedUser) {
        const { password, password_confirm, ...data } = formData;
        await authAPI.updateUser(selectedUser.id, password ? { ...data, password, password_confirm } : data);
        toast.success('Foydalanuvchi yangilandi!');
      } else {
        await authAPI.createUser(formData);
        toast.success('Foydalanuvchi yaratildi!');
      }
      setShowModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.username?.[0] || error.response?.data?.password?.[0] || 'Xatolik yuz berdi');
    }
  };

  const handleDelete = async () => {
    try {
      await authAPI.deleteUser(selectedUser.id);
      toast.success('Foydalanuvchi o\'chirildi!');
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error('Xatolik yuz berdi');
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      username: user.username, password: '', password_confirm: '', first_name: user.first_name || '', last_name: user.last_name || '',
      email: user.email || '', phone: user.phone || '', role: user.role, organization: user.organization || '',
      position: user.position || '', is_active: user.is_active
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setSelectedUser(null);
    setFormData({ username: '', password: '', password_confirm: '', first_name: '', last_name: '', email: '', phone: '', role: 'otm_auditor', organization: '', position: '', is_active: true });
  };

  const filteredUsers = users.filter(u => {
    const matchSearch = u.username.toLowerCase().includes(search.toLowerCase()) || u.first_name?.toLowerCase().includes(search.toLowerCase()) || u.last_name?.toLowerCase().includes(search.toLowerCase());
    const matchRole = !roleFilter || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  if (loading) return <Loading />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Foydalanuvchilar</h1>
          <p className="text-gray-500">Jami {users.length} ta foydalanuvchi</p>
        </div>
        <button onClick={() => { resetForm(); setShowModal(true); }} className="btn-primary">
          <PlusIcon className="w-5 h-5" /> Yangi foydalanuvchi
        </button>
      </div>

      <div className="card">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" placeholder="Qidirish..." className="input pl-10" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
          <select className="select w-auto" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <option value="">Barcha rollar</option>
            {Object.entries(USER_ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>

      {filteredUsers.length > 0 ? (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Foydalanuvchi</th>
                  <th className="table-header">Rol</th>
                  <th className="table-header hidden md:table-cell">Tashkilot</th>
                  <th className="table-header hidden sm:table-cell">Holat</th>
                  <th className="table-header w-24"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td className="table-cell">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                          {user.first_name?.[0] || user.username[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{user.first_name} {user.last_name || user.username}</p>
                          <p className="text-sm text-gray-500">@{user.username}</p>
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <span className={`badge ${user.role === 'admin' ? 'badge-purple' : user.role === 'otm_auditor' ? 'badge-info' : 'badge-gray'}`}>
                        {USER_ROLES[user.role]}
                      </span>
                    </td>
                    <td className="table-cell hidden md:table-cell text-gray-500">{user.organization_name || '-'}</td>
                    <td className="table-cell hidden sm:table-cell">
                      <span className={`badge ${user.is_active ? 'badge-success' : 'badge-gray'}`}>
                        {user.is_active ? 'Faol' : 'Nofaol'}
                      </span>
                    </td>
                    <td className="table-cell">
                      <div className="flex gap-1">
                        <button onClick={() => handleEdit(user)} className="p-2 hover:bg-gray-100 rounded-lg">
                          <PencilIcon className="w-4 h-4 text-gray-600" />
                        </button>
                        <button onClick={() => { setSelectedUser(user); setShowDeleteModal(true); }} className="p-2 hover:bg-gray-100 rounded-lg">
                          <TrashIcon className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <EmptyState icon={UsersIcon} title="Foydalanuvchilar topilmadi" />
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={selectedUser ? 'Foydalanuvchini tahrirlash' : 'Yangi foydalanuvchi'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="input-group">
              <label className="label">Username *</label>
              <input type="text" className="input" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} required disabled={!!selectedUser} />
            </div>
            <div className="input-group">
              <label className="label">{selectedUser ? 'Yangi parol' : 'Parol *'}</label>
              <input type="password" className="input" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required={!selectedUser} minLength={8} />
            </div>
            <div className="input-group">
              <label className="label">{selectedUser ? 'Parolni tasdiqlang' : 'Parolni tasdiqlang *'}</label>
              <input type="password" className="input" value={formData.password_confirm} onChange={(e) => setFormData({ ...formData, password_confirm: e.target.value })} required={!selectedUser} minLength={8} />
            </div>
            <div className="input-group">
              <label className="label">Ism</label>
              <input type="text" className="input" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} />
            </div>
            <div className="input-group">
              <label className="label">Familiya</label>
              <input type="text" className="input" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} />
            </div>
            <div className="input-group">
              <label className="label">Email</label>
              <input type="email" className="input" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
            </div>
            <div className="input-group">
              <label className="label">Telefon</label>
              <input type="text" className="input" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
            </div>
            <div className="input-group">
              <label className="label">Rol *</label>
              <select className="select" value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} required>
                {Object.entries(USER_ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="input-group">
              <label className="label">Tashkilot</label>
              <select className="select" value={formData.organization} onChange={(e) => setFormData({ ...formData, organization: e.target.value })}>
                <option value="">Tanlang</option>
                {organizations.map(org => <option key={org.id} value={org.id}>{org.short_name || org.name}</option>)}
              </select>
            </div>
          </div>
          <div className="input-group">
            <label className="label">Lavozim</label>
            <input type="text" className="input" value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="w-4 h-4" />
            <span>Faol</span>
          </label>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Bekor qilish</button>
            <button type="submit" className="btn-primary">{selectedUser ? 'Saqlash' : 'Yaratish'}</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} onConfirm={handleDelete}
        title="Foydalanuvchini o'chirish" message={`"${selectedUser?.username}" foydalanuvchisini o'chirishni xohlaysizmi?`} confirmText="O'chirish" />
    </div>
  );
};

export default Users;
